import moment from 'moment-timezone';
import { asTimeZone, fromUtcToDeviceTimeZoneAsMoment, fromUtcToTimeZone } from '../utils/dateFormatter';
import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, getByIdQuery, getByDoubleQuery, getByTripleQuery } from '../utils/dbUtils';
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { randomOrderNumberGenerator } from "../utils/orderUtils";
import Customer from './Customer';
import Address from './Address';
import StatusHistory from './StatusHistory';
import { collection, documentId, endBefore, getAggregateFromServer, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, sum, where } from 'firebase/firestore';
import { db } from '../firebase';
import { toE164PhoneNumber, toPhoneNumberLocal } from '../utils/stringFormatter';
import { toLower } from 'lodash';
import Product from './Product';
import { getMatchingCouponDiscountRow } from '../utils/couponUtils';

class Order {
    static collectionName = 'orders';

    orderNumber = randomOrderNumberGenerator(15);
    orderedAt = null; // date the order is placed, can be a future date for renewal orders created in advance
    orderRefId = null; // external order reference id
    orderAMDRefId = null; // external order/visit reference id in advanced md
    subscriptionId = null;
    orderType = "ORDER"; // enums OrderTypes

    status = "DRAFT"; // enum OrderStatuses
    statusLastUpdatedAt = moment().utc().toDate();
    statusLastUpdatedBy = 'SYSTEM';
    statusHistory = []; // list of StatusHistory, historical changes to status

    billingStatus = "PENDING";
    billingStatusHistory = []; // list of StatusHistory, historical changes to status
    bypassBillingStatus = false;

    userRefId = null;
    userId = null; // userId of the customer
    coveredEntityId = null;
    customer = new Customer(null).toDto(); // customer details snapshot

    billingAddress = new Address(null).toDto();
    shippingAddress = new Address(null).toDto();

    customerIPAddress = null;
    customerUserAgent = null;
    customerReferrerUrl = null;

    customerNote = null; // note provided by the customer at the time they placed the order

    isPaid = false;
    paidAt = null; // date the order is paid for
    completedAt = null; // date the order is completed
    closedAt = null;
    paymentAmount = 0; // amount paid or reimbursed by insurance

    discountTotal = 0.0; // total discounts to be subtracted from order to calculate subtotal
    discountTax = 0.0; // tax to be subtracted from tax total

    shippingTotal = 0.0; // total for shipping method (w/o tax)
    shippingTax = 0.0; // tax for shipping method

    feeTotal = 0.0; // total for all fee line items (w/o tax)
    feeTax = 0.0; // tax for all fee line items

    cartTotal = 0.0; // total of all order line items (w/o tax)
    cartTax = 0.0; // tax for all order line items

    subtotal = 0.0; // subtotal for the order
    tax = 0.0; // total tax for the order
    total = 0.0; // overall order total

    lineItems = [];
    taxLines = [];
    shippingLines = [];
    shipments = [];
    feeLines = [];
    discountLines = [];
    refunds = [];
    payments = [];
    notes = [];
    metaData = []; // array of order meta data
    metadata = {}; // can store captureReview: { reason, heldAt, clearedAt, clearedBy } - a first order held because a capture may already have taken the money, until staff clear it; coveredByUnusedVoucher: { voucherIds, cancelledAt, cancelledBy } - a voucher order cancelled rather than charged because an unused paid voucher already covered it; cancelledOnSubscriptionDenial: { encounterId, cancelledAt } - an unpaid voucher order cancelled when a referred follow-up denied its subscription

    parentOrderId = null;
    relatedOrderIds = []; // includes parent order, refund orders
    groupId = null; // foreign reference ID to the orderGroups table. Used to group several orders together that should be shipped/fulfilled together, and paid for together via a stacked voucher that gets split and applied to all the orders within the group

    shipmentStatus = "PENDING"; // enum ShipmentStatuses
    shipmentStatusHistory = []; // list of StatusHistory, historical changes to status
    carrier = null; // shipping carrier name
    trackingNumber = null; // shipment tracking number
    shipmentRefId = null; // external shipment reference id
    postageCost = 0;
    tracking = null;

    source = null;
    affiliateId = null;
    subAffiliateId = null;
    campaignId = null;
    programId = null;

    conversionId = null;
    isConversion = false;

    affiliateInfo = null;
    deviceInfo = null;
    emailInfo = null;
    phoneInfo = null;

    requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
    isConfirmed = false;
    confirmedAt = null; // date the order is confirmed

    requiresClinicianApproval = false; // requires that the doctor prescribes/approves the product
    isApproved = false;
    approvedAt = null; // date the order is approved by the clinician

    requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt
    encounterId = null; // reference to the associated encounter/visit setup for this order
    encounterAt = null;

    requiresSurveys = false; // requires patient to fill out forms/surveys
    isSurveysCompleted = false;
    surveysCompletedAt = null;

    // require documents to be provided when ordering product
    requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes
    isRequiredDocumentsProvided = false;
    requiredDocumentsProvidedAt = null;

    sentEmails = [];

    forms = []; // array of OrderForm
    formsCount = 0;

    leadId = null;

    prescriptionId = null;

    repId = 'NONE'; // userId of customer representative

    titrationLevel = 0;
    fillNumber = 0; // 1-based fill number within the prescription group (copied from Prescription)

    includesUpcharge = false;
    upcharge = 0;

    pharmacyId = null; // pharmacy to use if this order is for prescription product

    testMode = false;

    payoutId = null;
    isPaidFor = false;
    paidForAt = null;
    paidForAmount = 0;

    // Lab test order fields
    userTestId = null;
    externalOrderId = null;
    panelIds = [];
    scheduleEntryId = null;

    constructor(order) {
        if (isObjectNotEmpty(order)) {
            if (isStringNotEmpty(order.orderNumber)) { this.orderNumber = order.orderNumber; }
            if (isNotNullOrUndefined(order.orderedAt)) { this.orderedAt = order.orderedAt; }
            if (isStringNotEmpty(order.orderRefId)) { this.orderRefId = order.orderRefId; }
            if (isStringNotEmpty(order.orderAMDRefId)) { this.orderAMDRefId = order.orderAMDRefId; }
            if (isStringNotEmpty(order.subscriptionId)) { this.subscriptionId = order.subscriptionId; }
            if (isStringNotEmpty(order.orderType)) { this.orderType = order.orderType; }

            if (isStringNotEmpty(order.status)) { this.status = order.status; }
            if (isListNotEmpty(order.statusHistory)) { this.statusHistory = order.statusHistory; }
            else if (isStringNotEmpty(this.status)) { this.statusHistory = StatusHistory.addNewStatus(this.status, [], isNotNullOrUndefined(order.orderedAt) ? order.orderedAt : null); }
            if (isNotNullOrUndefined(order.statusLastUpdatedAt)) { this.statusLastUpdatedAt = order.statusLastUpdatedAt; }
            else if (isListNotEmpty(this.statusHistory)) { this.statusLastUpdatedAt = this.statusHistory[this.statusHistory.length - 1].at; }
            if (isStringNotEmpty(order.statusLastUpdatedBy)) { this.statusLastUpdatedBy = order.statusLastUpdatedBy; }
            else if (isListNotEmpty(this.statusHistory)) { this.statusLastUpdatedBy = this.statusHistory[this.statusHistory.length - 1].by; }

            if (isStringNotEmpty(order.billingStatus)) { this.billingStatus = order.billingStatus; }
            if (isListNotEmpty(order.billingStatusHistory)) { this.billingStatusHistory = order.billingStatusHistory; }
            else if (isStringNotEmpty(this.billingStatus)) { this.billingStatusHistory = StatusHistory.addNewStatus(this.billingStatus, [], isNotNullOrUndefined(order.orderedAt) ? order.orderedAt : null); }

            if (isBooleanTrue(order.bypassBillingStatus)) { this.bypassBillingStatus = order.bypassBillingStatus; }

            if (isStringNotEmpty(order.userRefId)) { this.userRefId = order.userRefId; }
            if (isStringNotEmpty(order.userId)) { this.userId = order.userId; }
            if (isStringNotEmpty(order.coveredEntityId)) { this.coveredEntityId = order.coveredEntityId; }
            if (isObjectNotEmpty(order.customer)) { this.customer = order.customer; }

            if (isObjectNotEmpty(order.billingAddress)) { this.billingAddress = order.billingAddress; }
            if (isObjectNotEmpty(order.shippingAddress)) { this.shippingAddress = order.shippingAddress; }

            if (isStringNotEmpty(order.customerIPAddress)) { this.customerIPAddress = order.customerIPAddress; }
            if (isStringNotEmpty(order.customerUserAgent)) { this.customerUserAgent = order.customerUserAgent; }
            if (isStringNotEmpty(order.customerReferrerUrl)) { this.customerReferrerUrl = order.customerReferrerUrl; }

            if (isStringNotEmpty(order.customerNote)) { this.customerNote = order.customerNote; }

            if (isBooleanTrue(order.isPaid)) { this.isPaid = order.isPaid; }
            if (isNotNullOrUndefined(order.paidAt)) { this.paidAt = order.paidAt; }
            if (isNotNullOrUndefined(order.completedAt)) { this.completedAt = order.completedAt; }
            if (isNotNullOrUndefined(order.closedAt)) { this.closedAt = order.closedAt; }
            if (isNumberGreaterThanOrEqualToZero(order.paymentAmount)) { this.paymentAmount = order.paymentAmount; }

            if (isNumberGreaterThanOrEqualToZero(order.discountTotal)) { this.discountTotal = order.discountTotal; }
            if (isNumberGreaterThanOrEqualToZero(order.discountTax)) { this.discountTax = order.discountTax; }
            if (isNumberGreaterThanOrEqualToZero(order.shippingTotal)) { this.shippingTotal = order.shippingTotal; }
            if (isNumberGreaterThanOrEqualToZero(order.shippingTax)) { this.shippingTax = order.shippingTax; }
            if (isNumberGreaterThanOrEqualToZero(order.feeTotal)) { this.feeTotal = order.feeTotal; }
            if (isNumberGreaterThanOrEqualToZero(order.feeTax)) { this.feeTax = order.feeTax; }
            if (isNumberGreaterThanOrEqualToZero(order.cartTotal)) { this.cartTotal = order.cartTotal; }
            if (isNumberGreaterThanOrEqualToZero(order.cartTax)) { this.cartTax = order.cartTax; }
            if (isNumberGreaterThanOrEqualToZero(order.subtotal)) { this.subtotal = order.subtotal; }
            if (isNumberGreaterThanOrEqualToZero(order.tax)) { this.tax = order.tax; }
            if (isNumberGreaterThanOrEqualToZero(order.total)) { this.total = order.total; }

            if (isListNotEmpty(order.lineItems)) { this.lineItems = order.lineItems; }
            if (isListNotEmpty(order.taxLines)) { this.taxLines = order.taxLines; }
            if (isListNotEmpty(order.shippingLines)) { this.shippingLines = order.shippingLines; }
            if (isListNotEmpty(order.shipments)) { this.shipments = order.shipments; }
            if (isListNotEmpty(order.feeLines)) { this.feeLines = order.feeLines; }
            if (isListNotEmpty(order.discountLines)) { this.discountLines = order.discountLines; }
            if (isListNotEmpty(order.refunds)) { this.refunds = order.refunds; }
            if (isListNotEmpty(order.payments)) { this.payments = order.payments; }
            if (isListNotEmpty(order.notes)) { this.notes = order.notes; }
            if (isListNotEmpty(order.metaData)) { this.metaData = order.metaData; }
            if (isObjectNotEmpty(order.metadata)) { this.metadata = order.metadata; }

            if (isStringNotEmpty(order.parentOrderId)) { this.parentOrderId = order.parentOrderId; }
            if (isListNotEmpty(order.relatedOrderIds)) { this.relatedOrderIds = order.relatedOrderIds; }
            if (isStringNotEmpty(order.groupId)) { this.groupId = order.groupId; }

            if (isStringNotEmpty(order.shipmentStatus)) { this.shipmentStatus = order.shipmentStatus; }
            if (isListNotEmpty(order.shipmentStatusHistory)) { this.shipmentStatusHistory = order.shipmentStatusHistory; }
            else if (isStringNotEmpty(this.shipmentStatus)) { this.shipmentStatusHistory = StatusHistory.addNewStatus(this.shipmentStatus, [], isNotNullOrUndefined(order.orderedAt) ? order.orderedAt : null); }

            if (isStringNotEmpty(order.carrier)) { this.carrier = order.carrier; }
            if (isStringNotEmpty(order.trackingNumber)) { this.trackingNumber = order.trackingNumber; }
            if (isStringNotEmpty(order.shipmentRefId)) { this.shipmentRefId = order.shipmentRefId; }
            if (isNumberNotEmpty(order.postageCost)) { this.postageCost = order.postageCost; }
            if (isObjectNotEmpty(order.tracking)) { this.tracking = order.tracking; }

            if (isStringNotEmpty(order.source)) { this.source = order.source; }
            if (isStringNotEmpty(order.affiliateId)) { this.affiliateId = toLower(order.affiliateId); }
            if (isStringNotEmpty(order.subAffiliateId)) { this.subAffiliateId = order.subAffiliateId; }
            if (isStringNotEmpty(order.campaignId)) { this.campaignId = order.campaignId; }
            if (isStringNotEmpty(order.programId)) { this.programId = order.programId; }

            if (isBooleanTrue(order.requiresConfirmation)) { this.requiresConfirmation = true; }
            if (isBooleanTrue(order.isConfirmed)) { this.isConfirmed = true; }
            if (isNotNullOrUndefined(order.confirmedAt)) { this.confirmedAt = order.confirmedAt; }

            if (isBooleanTrue(order.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
            if (isBooleanTrue(order.isApproved)) { this.isApproved = true; }
            if (isNotNullOrUndefined(order.approvedAt)) { this.approvedAt = order.approvedAt; }

            if (isBooleanTrue(order.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
            if (isStringNotEmpty(order.encounterId)) { this.encounterId = order.encounterId; }
            if (isNotNullOrUndefined(order.encounterAt)) { this.encounterAt = order.encounterAt; }

            if (isBooleanTrue(order.requiresSurveys)) { this.requiresSurveys = true; }
            if (isBooleanTrue(order.isSurveysCompleted)) { this.isSurveysCompleted = true; }
            if (isNotNullOrUndefined(order.surveysCompletedAt)) { this.surveysCompletedAt = order.surveysCompletedAt; }

            if (isListNotEmpty(order.requiredDocuments)) { this.requiredDocuments = order.requiredDocuments; }
            if (isBooleanTrue(order.isRequiredDocumentsProvided)) { this.isRequiredDocumentsProvided = true; }
            if (isNotNullOrUndefined(order.requiredDocumentsProvidedAt)) { this.requiredDocumentsProvidedAt = order.requiredDocumentsProvidedAt; }

            if (isListNotEmpty(order.sentEmails)) { this.sentEmails = order.sentEmails; }

            if (isListNotEmpty(order.forms)) {
                this.forms = order.forms;
                this.formsCount = order.forms.length;
            }

            if (isStringNotEmpty(order.conversionId)) { this.conversionId = order.conversionId; }
            if (isBooleanTrue(order.isConversion)) { this.isConversion = true; }

            if (isObjectNotEmpty(order.affiliateInfo)) { this.affiliateInfo = order.affiliateInfo; }
            if (isObjectNotEmpty(order.deviceInfo)) { this.deviceInfo = order.deviceInfo; }
            if (isObjectNotEmpty(order.emailInfo)) { this.emailInfo = order.emailInfo; }
            if (isObjectNotEmpty(order.phoneInfo)) { this.phoneInfo = order.phoneInfo; }

            if (isStringNotEmpty(order.leadId)) { this.leadId = order.leadId; }

            if (isStringNotEmpty(order.prescriptionId)) { this.prescriptionId = order.prescriptionId; }

            if (isStringNotEmpty(order.repId)) { this.repId = order.repId; }

            if (isNumberNotEmpty(order.titrationLevel)) { this.titrationLevel = order.titrationLevel; }
            if (isNumberGreaterThanOrEqualToZero(order.fillNumber)) { this.fillNumber = order.fillNumber; }

            if (isBooleanTrue(order.includesUpcharge)) { this.includesUpcharge = true; }
            if (isNumberGreaterThanOrEqualToZero(order.upcharge)) { this.upcharge = order.upcharge; }

            if (isStringNotEmpty(order.pharmacyId)) { this.pharmacyId = order.pharmacyId; }

            if (isBooleanTrue(order.testMode)) { this.testMode = true; }

            if (isStringNotEmpty(order.payoutId)) { this.payoutId = order.payoutId; }
            if (isBooleanTrue(order.isPaidFor)) { this.isPaidFor = true; }
            if (isNotNullOrUndefined(order.paidForAt)) { this.paidForAt = order.paidForAt; }
            if (isNumberNotEmpty(order.paidForAmount)) { this.paidForAmount = order.paidForAmount; }

            // Lab test order fields
            if (isStringNotEmpty(order.userTestId)) { this.userTestId = order.userTestId; }
            if (isStringNotEmpty(order.externalOrderId)) { this.externalOrderId = order.externalOrderId; }
            if (isListNotEmpty(order.panelIds)) { this.panelIds = order.panelIds; }
            if (isStringNotEmpty(order.scheduleEntryId)) { this.scheduleEntryId = order.scheduleEntryId; }
        }
    }

    toDto = () => {
        return {
            orderNumber: this.orderNumber,
            orderedAt: this.orderedAt,
            orderRefId: this.orderRefId,
            orderAMDRefId: this.orderAMDRefId,
            subscriptionId: this.subscriptionId,
            orderType: this.orderType,

            status: this.status,
            statusLastUpdatedAt: this.statusLastUpdatedAt,
            statusLastUpdatedBy: this.statusLastUpdatedBy,
            statusHistory: this.statusHistory,

            billingStatus: this.billingStatus,
            billingStatusHistory: this.billingStatusHistory,
            bypassBillingStatus: this.bypassBillingStatus,

            userRefId: this.userRefId,
            userId: this.userId,
            coveredEntityId: this.coveredEntityId,
            customer: this.customer,

            billingAddress: this.billingAddress,
            shippingAddress: this.shippingAddress,

            customerIPAddress: this.customerIPAddress,
            customerUserAgent: this.customerUserAgent,
            customerReferrerUrl: this.customerReferrerUrl,

            customerNote: this.customerNote,

            isPaid: this.isPaid,
            paidAt: this.paidAt,
            completedAt: this.completedAt,
            closedAt: this.closedAt,
            paymentAmount: this.paymentAmount,

            discountTotal: this.discountTotal,
            discountTax: this.discountTax,

            shippingTotal: this.shippingTotal,
            shippingTax: this.shippingTax,

            feeTotal: this.feeTotal,
            feeTax: this.feeTax,

            cartTotal: this.cartTotal,
            cartTax: this.cartTax,

            subtotal: this.subtotal,
            tax: this.tax,
            total: this.total,

            lineItems: this.lineItems,
            taxLines: this.taxLines,
            shippingLines: this.shippingLines,
            shipments: this.shipments,
            feeLines: this.feeLines,
            discountLines: this.discountLines,
            refunds: this.refunds,
            payments: this.payments,
            notes: this.notes,
            metaData: this.metaData,
            metadata: this.metadata,

            parentOrderId: this.parentOrderId,
            relatedOrderIds: this.relatedOrderIds,
            groupId: this.groupId,

            shipmentStatus: this.shipmentStatus,
            shipmentStatusHistory: this.shipmentStatusHistory,
            carrier: this.carrier,
            trackingNumber: this.trackingNumber,
            shipmentRefId: this.shipmentRefId,
            postageCost: this.postageCost,
            tracking: this.tracking,

            source: this.source,
            affiliateId: this.affiliateId,
            subAffiliateId: this.subAffiliateId,
            campaignId: this.campaignId,
            programId: this.programId,

            requiresConfirmation: this.requiresConfirmation,
            isConfirmed: this.isConfirmed,
            confirmedAt: this.confirmedAt,

            requiresClinicianApproval: this.requiresClinicianApproval,
            isApproved: this.isApproved,
            approvedAt: this.approvedAt,

            requiresClinicianEncounter: this.requiresClinicianEncounter,
            encounterId: this.encounterId,
            encounterAt: this.encounterAt,

            requiresSurveys: this.requiresSurveys,
            isSurveysCompleted: this.isSurveysCompleted,
            surveysCompletedAt: this.surveysCompletedAt,

            requiredDocuments: this.requiredDocuments,
            isRequiredDocumentsProvided: this.isRequiredDocumentsProvided,
            requiredDocumentsProvidedAt: this.requiredDocumentsProvidedAt,

            sentEmails: this.sentEmails,

            forms: this.forms,
            formsCount: this.formsCount,

            conversionId: this.conversionId,
            isConversion: this.isConversion,

            affiliateInfo: this.affiliateInfo,
            deviceInfo: this.deviceInfo,
            emailInfo: this.emailInfo,
            phoneInfo: this.phoneInfo,

            leadId: this.leadId,

            prescriptionId: this.prescriptionId,

            repId: this.repId,

            titrationLevel: this.titrationLevel,
            fillNumber: this.fillNumber,

            includesUpcharge: this.includesUpcharge,
            upcharge: this.upcharge,

            pharmacyId: this.pharmacyId,

            testMode: this.testMode,

            payoutId: this.payoutId,
            isPaidFor: this.isPaidFor,
            paidForAt: this.paidForAt,
            paidForAmount: this.paidForAmount,

            userTestId: this.userTestId,
            externalOrderId: this.externalOrderId,
            panelIds: this.panelIds,
            scheduleEntryId: this.scheduleEntryId
        }
    }

    isValid = () => {
        if (isStringEmpty(this.userId)) {
            throw new Error('userId is required.');
        }

        return true;
    }

    static getDiscountLinesTotal = (discountLines) => {
        return discountLines.reduce((total, line) => total + line.discountTotal, 0);
    }

    static getDiscountLinesTax = (discountLines) => {
        return discountLines.reduce((total, line) => total + line.discountTax, 0);
    }

    static getFeeLinesTotal = (feeLines) => {
        return feeLines.reduce((total, line) => total + line.subtotal, 0);
    }

    static getFeeLinesTax = (feeLines) => {
        return feeLines.reduce((total, line) => total + line.tax, 0);
    }

    static getShippingLinesTotal = (shippingLines) => {
        return shippingLines.reduce((total, line) => total + line.subtotal, 0);
    }

    static getShippingLinesTax = (shippingLines) => {
        return shippingLines.reduce((total, line) => total + line.tax, 0);
    }

    static getLineItemsTotal = (lineItems) => {
        return lineItems.reduce((total, item) => total + item.subtotal, 0);
    }

    static getLineItemsTaxTotal = (lineItems) => {
        return lineItems.reduce((total, item) => total + item.tax, 0);
    }

    static getLineItemTaxTotal = (product, quantity, taxRate) => {
        if (isObjectNotEmpty(product)) {
            let productObj = new Product(product);
            if (['TAXABLE', 'PRODUCT_ONLY'].includes(product.taxStatus)) {
                return (taxRate / 100) * (productObj.getPrice() * quantity);
            }
        }

        return 0.0;
    }

    static getMatchingCouponDiscountForLineItem = (coupon, lineItem, startingVariant = null, isFirstOrder = false) => {
        if (!isObjectNotEmpty(coupon) || !isObjectNotEmpty(lineItem)) {
            return null;
        }

        return getMatchingCouponDiscountRow(coupon?.discounts, {
            productId: lineItem?.productId ?? null,
            productSku: lineItem?.sku ?? null,
            variantId: lineItem?.variantId ?? startingVariant?.id ?? null,
            allowTargetOnlyFallback: isBooleanFalse(isFirstOrder) && isNullOrUndefined(startingVariant),
        });
    }

    static getMixedOrderDiscount = (coupon, lineItems, taxRate, startingVariant = null, isFirstOrder = false) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;

        lineItems.forEach((cartCouponItem) => {
            const cartCouponItemDiscount = Order.getMatchingCouponDiscountForLineItem(coupon, cartCouponItem, startingVariant, isFirstOrder);
            if (isObjectNotEmpty(cartCouponItemDiscount)) {
                const cartCouponItemSubtotal = Order.getLineItemsTotal([cartCouponItem]);
                const cartCouponItemTax = Order.getLineItemsTaxTotal([cartCouponItem], taxRate);

                if (cartCouponItemDiscount.discountType === 'FLAT_AMOUNT') {
                    if (cartCouponItemSubtotal >= cartCouponItemDiscount.discountAmount) {
                        discountAmount += cartCouponItemDiscount.discountAmount;
                        discountTaxAmount += (cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - cartCouponItemDiscount.discountAmount) / cartCouponItemSubtotal)));
                    } else {
                        discountAmount += cartCouponItemSubtotal;
                        discountTaxAmount += cartCouponItemTax;
                    }
                } else {
                    discountAmount += (cartCouponItemDiscount.discountAmount * cartCouponItemSubtotal);
                    discountTaxAmount += (cartCouponItemDiscount.discountAmount * cartCouponItemTax);
                }
            }
        });

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static getOrderDiscount = (coupon, lineItems, taxRate, bundlePaymentCount = 1, userId = null, email = null, startingVariant = null, isFirstOrder = false) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;
        // console.log('coupon', coupon);
        let cartTotal = Order.getLineItemsTotal(lineItems);
        let cartTax = Order.getLineItemsTaxTotal(lineItems, taxRate);
        if (isObjectNotEmpty(coupon) && isNumberGreaterThanZero(cartTotal)) {
            if (isBooleanTrue(coupon.active)) {
                if (coupon.usageLimit === 0 || (coupon.usageLimit > 0 && coupon.usageCount < coupon.usageLimit)) {
                    if (isNullOrUndefined(coupon.expiresAt) || (asTimeZone(coupon.expiresAt, 'US/Pacific') > moment.tz('US/Pacific'))) {
                        if (coupon.usageLimitPerUser === 0 || (isStringNotEmpty(userId) && coupon.usageLimitPerUser > coupon?.usedBy.filter(i => i === userId).length)) {
                            if (isListEmpty(coupon.emailRestrictions) || (isListNotEmpty(coupon.emailRestrictions) && isStringNotEmpty(email) && coupon.emailRestrictions.some(i => toLower(i) === toLower(email)))) {
                                if (isListEmpty(coupon.excludedProductIds) || (isListNotEmpty(coupon.excludedProductIds) && !lineItems.some(i => coupon.excludedProductIds.includes(i.productId)))) {
                                    if (isListEmpty(coupon.excludedVariantIds) || (isListNotEmpty(coupon.excludedVariantIds) && ((isObjectNotEmpty(startingVariant) && isStringNotEmpty(startingVariant.id) && !coupon.excludedVariantIds.includes(startingVariant.id)) || !lineItems.some(i => coupon.excludedVariantIds.includes(i.variantId))))) {
                                        if ((isBooleanFalse(coupon.applyToBundle) && bundlePaymentCount === 1) || (isBooleanTrue(coupon.applyToBundle) && (isListEmpty(coupon.excludedBundleCounts) || (isListNotEmpty(coupon.excludedBundleCounts) && !coupon.excludedBundleCounts.includes(bundlePaymentCount))))) {
                                            if (coupon.discountTarget === 'CART' && isBooleanTrue(isFirstOrder)) {
                                                if (coupon.discountType === 'FLAT_AMOUNT') {
                                                    discountAmount = coupon.discountAmount;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    // spread discount across cartItems and then reduce tax accordingly for taxable items
                                                    discountTaxAmount = cartTax - (cartTax * ((cartTotal - coupon.discountAmount) / cartTotal));
                                                } else {
                                                    discountAmount = coupon.discountAmount * cartTotal;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    discountTaxAmount = coupon.discountAmount * cartTax;
                                                }
                                            } else if ((coupon.discountTarget === 'PRODUCT') && isListNotEmpty(coupon.productIds) && isBooleanTrue(isFirstOrder)) {
                                                let cartCouponItems = lineItems.filter(i => coupon.productIds.includes(i.productId));
                                                if (isListNotEmpty(cartCouponItems)) {
                                                    // cart contains at least some of the product(s) that this discount applies to
                                                    let cartCouponItemSubtotal = Order.getLineItemsTotal(cartCouponItems);
                                                    let cartCouponItemTax = Order.getLineItemsTaxTotal(cartCouponItems, taxRate);
                                                    // console.log(cartCouponItemSubtotal);
                                                    if (coupon.discountType === 'FLAT_AMOUNT') {
                                                        if (cartCouponItemSubtotal >= coupon.discountAmount) {
                                                            discountAmount = coupon.discountAmount;

                                                            discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - coupon.discountAmount) / cartCouponItemSubtotal));
                                                        } else {
                                                            // if discount is larger than cart total, cap discount at cart total
                                                            discountAmount = cartCouponItemSubtotal;

                                                            discountTaxAmount = cartCouponItemTax;
                                                        }
                                                    } else {
                                                        discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                        discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                                    }
                                                }
                                            } else if (((coupon.discountTarget === 'MIXED' && isBooleanTrue(isFirstOrder)) || coupon.discountTarget === 'MIXED_RECURRING') && isListNotEmpty(coupon.discounts)) {
                                                let cartCouponItems = lineItems.filter(i => isObjectNotEmpty(Order.getMatchingCouponDiscountForLineItem(coupon, i, startingVariant, isFirstOrder)));
                                                if (isListNotEmpty(cartCouponItems)) {
                                                    const mixedDiscount = Order.getMixedOrderDiscount(coupon, cartCouponItems, taxRate, startingVariant, isFirstOrder);
                                                    discountAmount = mixedDiscount.discountTotal;
                                                    discountTaxAmount = mixedDiscount.discountTax;
                                                }
                                            } else if (coupon.discountTarget === 'RECURRING') {
                                                if (coupon.discountType === 'FLAT_AMOUNT') {
                                                    discountAmount = coupon.discountAmount;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    // spread discount across cartItems and then reduce tax accordingly for taxable items
                                                    discountTaxAmount = cartTax - (cartTax * ((cartTotal - coupon.discountAmount) / cartTotal));
                                                } else {
                                                    discountAmount = coupon.discountAmount * cartTotal;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    discountTaxAmount = coupon.discountAmount * cartTax;
                                                }
                                            } else if ((coupon.discountTarget === 'BUNDLE') && isListNotEmpty(coupon.productIds)) {
                                                let cartCouponItems = lineItems.filter(i => coupon.productIds.includes(i.productId));
                                                if (isListNotEmpty(cartCouponItems)) {
                                                    // cart contains at least some of the product(s) that this discount applies to
                                                    let cartCouponItemSubtotal = Order.getLineItemsTotal(cartCouponItems) * bundlePaymentCount;
                                                    let cartCouponItemTax = Order.getLineItemsTaxTotal(cartCouponItems, taxRate) * bundlePaymentCount;
                                                    // console.log(cartCouponItemSubtotal);
                                                    if (coupon.discountType === 'FLAT_AMOUNT') {
                                                        let bundleDiscountAmount = cartCouponItems.reduce((total, item) => total + (coupon.discountAmount * item.quantity * bundlePaymentCount), 0);
                                                        if (cartCouponItemSubtotal >= bundleDiscountAmount) {
                                                            discountAmount = bundleDiscountAmount;

                                                            discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - bundleDiscountAmount) / cartCouponItemSubtotal));
                                                        } else {
                                                            // if discount is larger than cart total, cap discount at cart total
                                                            discountAmount = cartCouponItemSubtotal;

                                                            discountTaxAmount = cartCouponItemTax;
                                                        }
                                                    } else {
                                                        discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                        discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static getPartialOrderDiscount = (coupon, allLineItems, lineItems, taxRate, bundlePaymentCount = 1, userId = null, email = null, startingVariant = null, isFirstOrder = false) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;
        // console.log(coupon);
        let partialCartTotal = Order.getLineItemsTotal(lineItems);
        let partialCartTax = Order.getLineItemsTaxTotal(lineItems, taxRate);
        let cartTotal = Order.getLineItemsTotal(allLineItems);
        let cartTax = Order.getLineItemsTaxTotal(allLineItems, taxRate);
        if (isObjectNotEmpty(coupon) && isNumberGreaterThanZero(cartTotal)) {
            if (isBooleanTrue(coupon.active)) {
                if (coupon.usageLimit === 0 || (coupon.usageLimit > 0 && coupon.usageCount < coupon.usageLimit)) {
                    if (isNullOrUndefined(coupon.expiresAt) || (asTimeZone(coupon.expiresAt, 'US/Pacific') > moment.tz('US/Pacific'))) {
                        if (coupon.usageLimitPerUser === 0 || (isStringNotEmpty(userId) && coupon.usageLimitPerUser > coupon?.usedBy.filter(i => i === userId).length)) {
                            if (isListEmpty(coupon.emailRestrictions) || (isListNotEmpty(coupon.emailRestrictions) && isStringNotEmpty(email) && coupon.emailRestrictions.some(i => toLower(i) === toLower(email)))) {
                                if (isListEmpty(coupon.excludedProductIds) || (isListNotEmpty(coupon.excludedProductIds) && !lineItems.some(i => coupon.excludedProductIds.includes(i.productId)))) {
                                    if (isListEmpty(coupon.excludedVariantIds) || (isListNotEmpty(coupon.excludedVariantIds) && ((isObjectNotEmpty(startingVariant) && isStringNotEmpty(startingVariant.id) && !coupon.excludedVariantIds.includes(startingVariant.id)) || !lineItems.some(i => coupon.excludedVariantIds.includes(i.variantId))))) {
                                        if ((isBooleanFalse(coupon.applyToBundle) && bundlePaymentCount === 1) || (isBooleanTrue(coupon.applyToBundle) && (isListEmpty(coupon.excludedBundleCounts) || (isListNotEmpty(coupon.excludedBundleCounts) && !coupon.excludedBundleCounts.includes(bundlePaymentCount))))) {
                                            if (coupon.discountTarget === 'CART' && isBooleanTrue(isFirstOrder)) {
                                                if (coupon.discountType === 'FLAT_AMOUNT') {
                                                    let partialCouponDiscountAmount = coupon.discountAmount * (partialCartTotal / cartTotal);
                                                    discountAmount = partialCouponDiscountAmount;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    // spread discount across cartItems and then reduce tax accordingly for taxable items
                                                    discountTaxAmount = partialCartTax - (partialCartTax * ((partialCartTotal - partialCouponDiscountAmount) / partialCartTotal));
                                                } else {
                                                    discountAmount = coupon.discountAmount * partialCartTotal;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    discountTaxAmount = coupon.discountAmount * partialCartTax;
                                                }
                                            } else if ((coupon.discountTarget === 'PRODUCT') && isListNotEmpty(coupon.productIds) && isBooleanTrue(isFirstOrder)) {
                                                let cartCouponItems = lineItems.filter(i => coupon.productIds.includes(i.productId));
                                                if (isListNotEmpty(cartCouponItems)) {
                                                    // cart contains at least some of the product(s) that this discount applies to
                                                    let cartCouponItemSubtotal = Order.getLineItemsTotal(cartCouponItems);
                                                    let cartCouponItemTax = Order.getLineItemsTaxTotal(cartCouponItems, taxRate);
                                                    // console.log(cartCouponItemSubtotal);
                                                    if (coupon.discountType === 'FLAT_AMOUNT') {
                                                        let partialCouponDiscountAmount = coupon.discountAmount * (cartCouponItemSubtotal / cartTotal);
                                                        if (cartCouponItemSubtotal >= partialCouponDiscountAmount) {
                                                            discountAmount = partialCouponDiscountAmount;

                                                            discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - partialCouponDiscountAmount) / cartCouponItemSubtotal));
                                                        } else {
                                                            // if discount is larger than cart total, cap discount at cart total
                                                            discountAmount = cartCouponItemSubtotal;

                                                            discountTaxAmount = cartCouponItemTax;
                                                        }
                                                    } else {
                                                        discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                        discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                                    }
                                                }
                                            } else if (((coupon.discountTarget === 'MIXED' && isBooleanTrue(isFirstOrder)) || coupon.discountTarget === 'MIXED_RECURRING') && isListNotEmpty(coupon.discounts)) {
                                                let cartCouponItems = lineItems.filter(i => isObjectNotEmpty(Order.getMatchingCouponDiscountForLineItem(coupon, i, startingVariant, isFirstOrder)));
                                                if (isListNotEmpty(cartCouponItems)) {
                                                    const mixedDiscount = Order.getMixedOrderDiscount(coupon, cartCouponItems, taxRate, startingVariant, isFirstOrder);
                                                    discountAmount = mixedDiscount.discountTotal;
                                                    discountTaxAmount = mixedDiscount.discountTax;
                                                }
                                            } else if (coupon.discountTarget === 'RECURRING') {
                                                if (coupon.discountType === 'FLAT_AMOUNT') {
                                                    let partialCouponDiscountAmount = coupon.discountAmount * (partialCartTotal / cartTotal);

                                                    discountAmount = partialCouponDiscountAmount;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    // spread discount across cartItems and then reduce tax accordingly for taxable items
                                                    discountTaxAmount = partialCartTax - (partialCartTax * ((partialCartTotal - partialCouponDiscountAmount) / partialCartTotal));
                                                } else {
                                                    discountAmount = coupon.discountAmount * partialCartTotal;

                                                    // calculate tax reduction for discount across all taxable cartItems
                                                    discountTaxAmount = coupon.discountAmount * partialCartTax;
                                                }
                                            } else if ((coupon.discountTarget === 'BUNDLE') && isListNotEmpty(coupon.productIds)) {
                                                let cartCouponItems = lineItems.filter(i => coupon.productIds.includes(i.productId));
                                                if (isListNotEmpty(cartCouponItems)) {
                                                    // cart contains at least some of the product(s) that this discount applies to
                                                    let cartCouponItemSubtotal = Order.getLineItemsTotal(cartCouponItems) * bundlePaymentCount;
                                                    let cartCouponItemTax = Order.getLineItemsTaxTotal(cartCouponItems, taxRate) * bundlePaymentCount;
                                                    // console.log(cartCouponItemSubtotal);
                                                    if (coupon.discountType === 'FLAT_AMOUNT') {
                                                        let bundleDiscountAmount = cartCouponItems.reduce((total, item) => total + (coupon.discountAmount * item.quantity * bundlePaymentCount), 0);
                                                        if (cartCouponItemSubtotal >= bundleDiscountAmount) {
                                                            discountAmount = bundleDiscountAmount;

                                                            discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - bundleDiscountAmount) / cartCouponItemSubtotal));
                                                        } else {
                                                            // if discount is larger than cart total, cap discount at cart total
                                                            discountAmount = cartCouponItemSubtotal;

                                                            discountTaxAmount = cartCouponItemTax;
                                                        }
                                                    } else {
                                                        discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                        discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static calculateOrderSummary = (lineItems = [], discountLines = [], feeLines = [], shippingLines = []) => {
        let discountTotal = Order.getDiscountLinesTotal(discountLines);
        let discountTax = Order.getDiscountLinesTax(discountLines);

        let shippingTotal = Order.getShippingLinesTotal(shippingLines);
        let shippingTax = Order.getShippingLinesTax(shippingLines);

        let feeTotal = Order.getFeeLinesTotal(feeLines);
        let feeTax = Order.getFeeLinesTax(feeLines);

        let cartTotal = Order.getLineItemsTotal(lineItems);
        let cartTax = Order.getLineItemsTaxTotal(lineItems);

        let subtotal = cartTotal + shippingTotal + feeTotal - discountTotal;
        let tax = cartTax + shippingTax + feeTax - discountTax;
        let total = subtotal + tax;

        return {
            discountTotal: discountTotal,
            discountTax: discountTax,

            shippingTotal: shippingTotal,
            shippingTax: shippingTax,

            feeTotal: feeTotal,
            feeTax: feeTax,

            cartTotal: cartTotal,
            cartTax: cartTax,

            subtotal: subtotal,
            tax: tax,
            total: total
        };
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Order.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Order.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Order.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedOrder) => {
        const updatedObj = new Order(updatedOrder);
        if (updatedObj.isValid() && id === updatedOrder.id) {
            return await update(Order.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated order must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Order.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Order.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getByOrderRefId = async (orderRefId) => {
        if (isStringNotEmpty(orderRefId)) {
            return await getBySingleQuery(Order.collectionName, 'orderRefId', orderRefId, '==');
        } else {
            throw new Error("orderRefId is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Order.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Order.collectionName, fieldName, fieldValue, comparer);
    }

    static getByUserId = async (userId) => {
        return await getBySingleQuery(Order.collectionName, 'userId', userId, '==');
    }

    static getByUserIdAndOrderType = async (userId, orderType) => {
        return await getByDoubleQuery(Order.collectionName, 'userId', userId, '==', 'orderType', orderType, '==');
    }

    static getByUserIdAndStatus = async (userId, status) => {
        let queryConstraints = [];

        queryConstraints.push(where('userId', '==', userId));

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }


        let q = query(collection(db, Order.collectionName), ...queryConstraints);

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static getByUserIdAndStatusAndProgramId = async (userId, status, programId) => {
        return await getByTripleQuery(Order.collectionName, 'userId', userId, '==', 'status', status, '==', 'programId', programId, '==');
    }

    static getByCoveredEntityId = async (coveredEntityId) => {
        return await getBySingleQuery(Order.collectionName, 'coveredEntityId', coveredEntityId, '==');
    }

    static getByCoveredEntityIdAndUserId = async (coveredEntityId, userId) => {
        return await getByDoubleQuery(Order.collectionName, 'coveredEntityId', coveredEntityId, '==', 'userId', userId, '==');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Order.collectionName, ids, 'in');
    }

    static getBySubscriptionId = async (subscriptionId) => {
        return await getBySingleQuery(Order.collectionName, 'subscriptionId', subscriptionId, '==');
    }

    static getBySubscriptionIdAndStatus = async (subscriptionId, status) => {
        return await getByDoubleQuery(Order.collectionName, 'subscriptionId', subscriptionId, '==', 'status', status, '==');
    }

    static getBySubscriptionIdAndStatusAndOrderType = async (subscriptionId, status, orderType) => {
        return await getByTripleQuery(Order.collectionName, 'subscriptionId', subscriptionId, '==', 'status', status, '==', 'orderType', orderType, '==');
    }

    static getBySubscriptionIdAndProgramId = async (subscriptionId, programId) => {
        return await getByDoubleQuery(Order.collectionName, 'subscriptionId', subscriptionId, '==', 'programId', programId, '==');
    }

    static getByQueryConstraints = async (queryConstraints = []) => {
        let q = query(collection(db, Order.collectionName), ...queryConstraints);

        const snapShot = await getDocs(q);
        // console.log(snapShot)
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id, // append document id to each document
                ...doc.data()
            };
        });
    }

    static getByCoveredEntityIdAndPhone = async (coveredEntityId, phone) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getByCoveredEntityIdAndEmail = async (coveredEntityId, email) => {
        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }

        return await this.getByQueryConstraints(queryConstraints);
    }

    static search = async (searchParams) => {
        let { testMode, userId, subscriptionId, repId, programId, status, billingStatus, isPaid, orderType, coveredEntityId, pharmacyId, firstName, lastName, email, phone, orderId, orderRefId, orderNumber, prescriptionId, start, end } = searchParams;
        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', 'in', subscriptionId));
        } else if (isStringNotEmptyOrZeroLength(subscriptionId)) {
            queryConstraints.push(where('subscriptionId', '==', subscriptionId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(billingStatus)) {
            queryConstraints.push(where('billingStatus', 'in', billingStatus));
        } else if (isStringNotEmptyOrZeroLength(billingStatus)) {
            queryConstraints.push(where('billingStatus', '==', billingStatus));
        }

        if (isNotNullOrUndefined(isPaid)) {
            queryConstraints.push(where('isPaid', '==', isBooleanTrue(isPaid)));
        }

        if (isListNotEmpty(orderType)) {
            queryConstraints.push(where('orderType', 'in', orderType));
        } else if (isStringNotEmptyOrZeroLength(orderType)) {
            queryConstraints.push(where('orderType', '==', orderType));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isStringNotEmptyOrZeroLength(firstName)) {
            queryConstraints.push(where('customer.firstNameLower', '==', toLower(firstName)));
        }
        if (isStringNotEmptyOrZeroLength(lastName)) {
            queryConstraints.push(where('customer.lastNameLower', '==', toLower(lastName)));
        }
        if (isStringNotEmptyOrZeroLength(email)) {
            queryConstraints.push(where('customer.email', '==', toLower(email)));
        }
        if (isStringNotEmptyOrZeroLength(phone)) {
            const cleanPhone = toPhoneNumberLocal(phone);
            const formattedPhone = toE164PhoneNumber(phone);
            queryConstraints.push(where('customer.phone', 'in', [phone, formattedPhone, cleanPhone]));
        }
        if (isStringNotEmptyOrZeroLength(orderId)) {
            queryConstraints.push(where(documentId(), '==', orderId));
        }
        if (isStringNotEmptyOrZeroLength(orderRefId)) {
            queryConstraints.push(where('orderRefId', '==', orderRefId));
        }
        if (isStringNotEmptyOrZeroLength(orderNumber)) {
            queryConstraints.push(where('orderNumber', '==', orderNumber));
        }
        if (isStringNotEmptyOrZeroLength(prescriptionId)) {
            queryConstraints.push(where('prescriptionId', '==', prescriptionId));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '<=', endDate));
        }

        if (isStringEmpty(orderId) && isStringEmpty(orderRefId) && isStringEmpty(orderNumber) && isStringEmpty(prescriptionId)) {
            // if searching by specific order identifiers, order doesn't matter since results will be specific orders, 
            // but if not searching by specific order identifiers, order results by orderedAt asc
            queryConstraints.push(orderBy('createdAt', 'asc'));
        }
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getCount = async (queryParameters) => {
        let { testMode, userId, repId, status, billingStatus, isPaid, orderType, coveredEntityId, pharmacyId, programId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(billingStatus)) {
            queryConstraints.push(where('billingStatus', 'in', billingStatus));
        } else if (isStringNotEmptyOrZeroLength(billingStatus)) {
            queryConstraints.push(where('billingStatus', '==', billingStatus));
        }

        if (isBooleanTrue(isPaid)) {
            queryConstraints.push(where('isPaid', '==', true));
        } else if (isBooleanFalse(isPaid)) {
            queryConstraints.push(where('isPaid', '==', false));
        }

        if (isListNotEmpty(orderType)) {
            queryConstraints.push(where('orderType', 'in', orderType));
        } else if (isStringNotEmptyOrZeroLength(orderType)) {
            queryConstraints.push(where('orderType', '==', orderType));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '<=', endDate));
        }

        let q = query(collection(db, Order.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'orderedAt') => {
        let { testMode, userId, repId, status, billingStatus, isPaid, orderType, coveredEntityId, pharmacyId, programId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(programId)) {
            queryConstraints.push(where('programId', 'in', programId));
        } else if (isStringNotEmptyOrZeroLength(programId)) {
            queryConstraints.push(where('programId', '==', programId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(billingStatus)) {
            queryConstraints.push(where('billingStatus', 'in', billingStatus));
        } else if (isStringNotEmptyOrZeroLength(billingStatus)) {
            queryConstraints.push(where('billingStatus', '==', billingStatus));
        }

        if (isBooleanTrue(isPaid)) {
            queryConstraints.push(where('isPaid', '==', true));
        } else if (isBooleanFalse(isPaid)) {
            queryConstraints.push(where('isPaid', '==', false));
        }

        if (isListNotEmpty(orderType)) {
            queryConstraints.push(where('orderType', 'in', orderType));
        } else if (isStringNotEmptyOrZeroLength(orderType)) {
            queryConstraints.push(where('orderType', '==', orderType));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Order.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['repId'], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Order.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['repId'], lastVisible['id']));
        } else {
            q = query(collection(db, Order.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('repId', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Order.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await Order.getPaginated(queryParameters, 10000, null, next, 'orderedAt');

                // let results = list.map((item) => {
                //     return {
                //         ...item,
                //         orderedAt: toUtcISOStringWithTimeZone(item.orderedAt, 'US/Pacific'),
                //         paidAt: toUtcISOStringWithTimeZone(item.paidAt, 'US/Pacific'),
                //         completedAt: toUtcISOStringWithTimeZone(item.completedAt, 'US/Pacific'),
                //     };
                // });

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    static getSum = async (queryParameters, sumField = 'total') => {
        let { testMode, userId, repId, status, billingStatus, isPaid, orderType, coveredEntityId, pharmacyId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isBooleanTrue(testMode)) {
            queryConstraints.push(where('testMode', '==', true));
        } else if (isBooleanFalse(testMode)) {
            queryConstraints.push(where('testMode', '==', false));
        }

        if (isListNotEmpty(userId)) {
            queryConstraints.push(where('userId', 'in', userId));
        } else if (isStringNotEmptyOrZeroLength(userId)) {
            queryConstraints.push(where('userId', '==', userId));
        }

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(billingStatus)) {
            queryConstraints.push(where('billingStatus', 'in', billingStatus));
        } else if (isStringNotEmptyOrZeroLength(billingStatus)) {
            queryConstraints.push(where('billingStatus', '==', billingStatus));
        }

        if (isBooleanTrue(isPaid)) {
            queryConstraints.push(where('isPaid', '==', true));
        } else if (isBooleanFalse(isPaid)) {
            queryConstraints.push(where('isPaid', '==', false));
        }

        if (isListNotEmpty(orderType)) {
            queryConstraints.push(where('orderType', 'in', orderType));
        } else if (isStringNotEmptyOrZeroLength(orderType)) {
            queryConstraints.push(where('orderType', '==', orderType));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
        }

        if (isListNotEmpty(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', 'in', pharmacyId));
        } else if (isStringNotEmptyOrZeroLength(pharmacyId)) {
            queryConstraints.push(where('pharmacyId', '==', pharmacyId));
        }

        if (isListNotEmpty(repId)) {
            queryConstraints.push(where('repId', 'in', repId));
        } else if (isStringNotEmptyOrZeroLength(repId)) {
            if (repId === 'NONE') {
                queryConstraints.push(where('repId', '==', 'NONE'));
            } else if (repId === 'ASSIGNED') {
                queryConstraints.push(where('repId', '!=', 'NONE'));
            } else {
                queryConstraints.push(where('repId', '==', repId));
            }
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('orderedAt', '<=', endDate));
        }

        let q = query(collection(db, Order.collectionName), ...queryConstraints);

        const snapShot = await getAggregateFromServer(q, { totalSum: sum(sumField) });

        //console.log(snapShot.data());

        return snapShot.data().totalSum;
    }
};

export default Order;
